/**
 * Reg. (UE) 1379/2013, testo consolidato
 * (https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:02013R1379-20200425):
 * lessico e verifiche pure sull'ambito merceologico e sull'origine dei
 * prodotti della pesca e dell'acquacoltura — nessuna dipendenza da rete,
 * database o orologio.
 *
 * Copre due lacune segnalate in `docs/plans/prepackaged-D-origine.md` §13
 * (domande bloccanti 6 e 7) e chiuse da
 * `docs/plans/prepackaged-ERRATA-2026-08-20.md` §2 decisione 3 — «gli
 * specialisti sono i clienti di Prospero, non la sua via d'uscita: i
 * cataloghi mancanti vanno costruiti, non aggirati»:
 *
 * 1. Allegato I lett. a), b), c) ed e) — i codici della nomenclatura
 *    combinata che l'art. 35 assoggetta all'obbligo di informazione. La
 *    lett. d) (NC 0511 91 10 / 0511 91 90, cascami di pesci) resta fuori
 *    ambito: non è materia prima per un'etichetta di prodotto finito.
 * 2. Art. 38 §1 lett. a) — le zone di pesca FAO e le sottozone/divisioni
 *    che l'Atlantico nord-orientale (zona 27) e il Mediterraneo/Mar Nero
 *    (zona 37) richiedono in aggiunta alla zona, a differenza di tutte le
 *    altre. I nomi delle sottozone seguono il portale ufficiale della
 *    Commissione europea sui nomi commerciali dei prodotti della pesca
 *    (https://fish-commercial-names.ec.europa.eu/fish-names/fishing-areas/),
 *    costruito sulla classificazione FAO delle aree di pesca (CWP).
 *
 * Nessuna funzione qui blocca l'inserimento o solleva eccezioni: ogni
 * funzione restituisce un esito che il chiamante interpreta.
 */

// ---------------------------------------------------------------------------
// Art. 35 §1 lett. b) — metodo di produzione
// ---------------------------------------------------------------------------

export const FISHERY_PRODUCTION_METHODS = ['CAUGHT', 'CAUGHT_FRESHWATER', 'FARMED'] as const;

export type FisheryProductionMethod = (typeof FISHERY_PRODUCTION_METHODS)[number];

/**
 * Art. 35 §1 lett. b): i soli tre termini letterali ammessi, senza
 * sinonimi né traduzioni libere.
 */
export const FISHERY_PRODUCTION_METHOD_TERMS: Record<FisheryProductionMethod, string> = {
  CAUGHT: 'Pescato',
  CAUGHT_FRESHWATER: 'Pescato in acque dolci',
  FARMED: 'Allevato',
};

// ---------------------------------------------------------------------------
// Art. 35 §1 lett. c) — attrezzo da pesca, Allegato III prima colonna
// ---------------------------------------------------------------------------

export const FISHERY_GEAR_CATEGORIES = [
  'SEINE_NETS',
  'TRAWLS',
  'GILLNETS_AND_ENTANGLING_NETS',
  'SURROUNDING_NETS_AND_LIFT_NETS',
  'HOOKS_AND_LINES',
  'DREDGES',
  'POTS_AND_TRAPS',
] as const;

export type FisheryGearCategory = (typeof FISHERY_GEAR_CATEGORIES)[number];

/** Allegato III, prima colonna: dicitura italiana letterale, una per categoria. */
export const FISHERY_GEAR_CATEGORY_LABELS: Record<FisheryGearCategory, string> = {
  SEINE_NETS: 'Sciabiche',
  TRAWLS: 'Reti da traino',
  GILLNETS_AND_ENTANGLING_NETS: 'Reti da imbrocco e reti analoghe',
  SURROUNDING_NETS_AND_LIFT_NETS: 'Reti da circuizione e reti da raccolta',
  HOOKS_AND_LINES: 'Ami e palangari',
  DREDGES: 'Draghe',
  POTS_AND_TRAPS: 'Nasse e trappole',
};

// ---------------------------------------------------------------------------
// Allegato I lett. a), b), c), e) — ambito per codice NC
// ---------------------------------------------------------------------------

/**
 * Voci (4 cifre) dell'Allegato I lett. a) (0301-0304, pesci) e lett. c)
 * (0306-0307, crostacei, molluschi e altri invertebrati acquatici) e
 * lett. b) (0305, pesci essiccati/salati/affumicati e farine di pesce).
 * A questo livello l'intera voce è in ambito: qualunque sottovoce o codice
 * NC a 8 cifre che comincia con uno di questi 4 caratteri lo è.
 */
const FISHERY_SCOPE_HEADING_PREFIXES = [
  '0301',
  '0302',
  '0303',
  '0304',
  '0305',
  '0306',
  '0307',
] as const;

/**
 * Sottovoce (6 cifre) dell'Allegato I lett. e): solo le alghe (NC 1212 20 00),
 * non l'intera voce 1212 — che comprende anche canna e barbabietola da
 * zucchero (1212 91, 1212 93), fuori ambito.
 */
const FISHERY_SCOPE_SUBHEADING_PREFIXES = ['121220'] as const;

/** Riduce un codice NC alla sola sequenza di cifre, qualunque sia la punteggiatura in ingresso. */
export function normalizeCombinedNomenclatureCode(rawCode: string): string {
  return rawCode.replace(/[^0-9]/g, '');
}

/**
 * L'art. 35 si applica ai soli prodotti dell'Allegato I lett. a), b), c) ed
 * e). Un codice troppo corto per identificare la voce (meno di 4 cifre) non
 * permette di stabilire l'ambito e non è considerato in ambito: la funzione
 * non indovina un capitolo intero da un prefisso ambiguo.
 */
export function isNcCodeInFisheryLabelingScope(rawNcCode: string): boolean {
  const digits = normalizeCombinedNomenclatureCode(rawNcCode);
  if (digits.length < 4) {
    return false;
  }
  if (FISHERY_SCOPE_HEADING_PREFIXES.some((heading) => digits.startsWith(heading))) {
    return true;
  }
  if (digits.length < 6) {
    return false;
  }
  return FISHERY_SCOPE_SUBHEADING_PREFIXES.some((subheading) => digits.startsWith(subheading));
}

// ---------------------------------------------------------------------------
// Art. 38 §1 — zona di cattura o di allevamento
// ---------------------------------------------------------------------------

export interface FaoFishingArea {
  readonly code: string;
  readonly nameIt: string;
}

/**
 * Le 19 zone di pesca principali della classificazione FAO (CWP), che l'art.
 * 38 §1 lett. a) richiama come «zona di pesca della FAO» per il pescato in
 * mare. Fonte dei nomi italiani: portale ufficiale della Commissione europea
 * sui nomi commerciali dei prodotti della pesca.
 */
export const FAO_MAJOR_FISHING_AREAS: readonly FaoFishingArea[] = [
  { code: '18', nameIt: 'Mare Artico' },
  { code: '21', nameIt: 'Atlantico, nord-occidentale' },
  { code: '27', nameIt: 'Atlantico, nord-orientale' },
  { code: '31', nameIt: 'Atlantico, centro-occidentale' },
  { code: '34', nameIt: 'Atlantico, centro-orientale' },
  { code: '37', nameIt: 'Mediterraneo e Mar Nero' },
  { code: '41', nameIt: 'Atlantico, sud-occidentale' },
  { code: '47', nameIt: 'Atlantico, sud-orientale' },
  { code: '48', nameIt: 'Atlantico, antartico' },
  { code: '51', nameIt: 'Oceano Indiano, occidentale' },
  { code: '57', nameIt: 'Oceano Indiano, orientale' },
  { code: '58', nameIt: 'Oceano Indiano, antartico e meridionale' },
  { code: '61', nameIt: 'Pacifico, nord-occidentale' },
  { code: '67', nameIt: 'Pacifico, nord-orientale' },
  { code: '71', nameIt: 'Pacifico, centro-occidentale' },
  { code: '77', nameIt: 'Pacifico, centro-orientale' },
  { code: '81', nameIt: 'Pacifico, sud-occidentale' },
  { code: '87', nameIt: 'Pacifico, sud-orientale' },
  { code: '88', nameIt: 'Pacifico, antartico' },
];

const FAO_MAJOR_FISHING_AREA_CODES: ReadonlySet<string> = new Set(
  FAO_MAJOR_FISHING_AREAS.map((area) => area.code),
);

export function isFaoMajorFishingAreaCode(areaCode: string): boolean {
  return FAO_MAJOR_FISHING_AREA_CODES.has(areaCode);
}

/**
 * Art. 38 §1 lett. a): solo l'Atlantico nord-orientale (zona 27) e il
 * Mediterraneo/Mar Nero (zona 37) richiedono la sottozona o divisione oltre
 * alla zona; per «acque diverse» la deroga di legge basta alla sola
 * denominazione della zona FAO.
 */
const FAO_AREAS_REQUIRING_SUBZONE: ReadonlySet<string> = new Set(['27', '37']);

export function faoAreaRequiresSubzone(areaCode: string): boolean {
  return FAO_AREAS_REQUIRING_SUBZONE.has(areaCode);
}

export interface FaoFishingSubzone {
  readonly code: string;
  readonly areaCode: string;
  readonly nameIt: string;
}

/**
 * Sottozone (livello «sottozona», FAO major fishing area division) della
 * zona 27 — Atlantico nord-orientale. L'art. 38 chiede «la sottozona o
 * divisione»: dichiarare la sola sottozona è sufficiente, la divisione più
 * fine (es. 27.4.a) resta una precisione facoltativa che questo catalogo non
 * modella.
 */
export const FAO_AREA_27_SUBZONES: readonly FaoFishingSubzone[] = [
  { code: '27.1', areaCode: '27', nameIt: 'Mare di Barents' },
  { code: '27.2', areaCode: '27', nameIt: 'Mare di Norvegia, Spitzbergen e Isola degli Orsi' },
  { code: '27.3', areaCode: '27', nameIt: 'Skagerrak, Kattegat, Sund, Belt e Mar Baltico' },
  { code: '27.4', areaCode: '27', nameIt: 'Mare del Nord' },
  { code: '27.5', areaCode: '27', nameIt: 'Fondali islandesi e delle Fær Øer' },
  {
    code: '27.6',
    areaCode: '27',
    nameIt: 'Rockall, costa nord-occidentale di Scozia e Irlanda del Nord',
  },
  {
    code: '27.7',
    areaCode: '27',
    nameIt: "Mar d'Irlanda, ovest dell'Irlanda, Manica e Mar Celtico",
  },
  { code: '27.8', areaCode: '27', nameIt: 'Golfo di Guascogna' },
  { code: '27.9', areaCode: '27', nameIt: 'Acque portoghesi' },
  { code: '27.10', areaCode: '27', nameIt: 'Fondali delle Azzorre' },
  { code: '27.12', areaCode: '27', nameIt: 'Atlantico nord-orientale al largo' },
  { code: '27.14', areaCode: '27', nameIt: 'Groenlandia orientale' },
];

/** Sottozone della zona 37 — Mediterraneo e Mar Nero. */
export const FAO_AREA_37_SUBZONES: readonly FaoFishingSubzone[] = [
  { code: '37.1', areaCode: '37', nameIt: 'Mediterraneo occidentale' },
  { code: '37.2', areaCode: '37', nameIt: 'Mediterraneo centrale' },
  { code: '37.3', areaCode: '37', nameIt: 'Mediterraneo orientale' },
  { code: '37.4', areaCode: '37', nameIt: 'Mar Nero' },
];

const FAO_SUBZONES_BY_AREA: ReadonlyMap<string, readonly FaoFishingSubzone[]> = new Map([
  ['27', FAO_AREA_27_SUBZONES],
  ['37', FAO_AREA_37_SUBZONES],
]);

/** Le sottozone note per una zona; un array vuoto quando la zona non ne ha un catalogo. */
export function faoSubzonesForArea(areaCode: string): readonly FaoFishingSubzone[] {
  return FAO_SUBZONES_BY_AREA.get(areaCode) ?? [];
}

/**
 * Una combinazione zona/sottozona dichiarata è valida quando: la zona esiste;
 * se la zona richiede la sottozona (27 o 37), una sottozona di quella zona è
 * presente; se una sottozona è presente, appartiene alla zona dichiarata (e
 * quindi solo a zone che ne hanno un catalogo).
 */
export function isValidFaoAreaSubzoneCombination(
  areaCode: string,
  subzoneCode: string | null,
): boolean {
  if (!isFaoMajorFishingAreaCode(areaCode)) {
    return false;
  }
  if (subzoneCode === null) {
    return !faoAreaRequiresSubzone(areaCode);
  }
  return faoSubzonesForArea(areaCode).some((subzone) => subzone.code === subzoneCode);
}

// ---------------------------------------------------------------------------
// Indicazioni di origine dovute per metodo di produzione
// ---------------------------------------------------------------------------

export type FisheryOriginIndication =
  | 'FAO_AREA'
  | 'FAO_SUBZONE'
  | 'GEAR_CATEGORY'
  | 'WATER_BODY'
  | 'COUNTRY';

/**
 * Art. 35 §1 lett. c) incrociato con l'art. 38 §1: quali indicazioni di
 * origine il metodo di produzione dichiarato richiede. `faoAreaCode` è
 * opzionale — quando manca, `FAO_SUBZONE` non è richiesta perché la sua
 * necessità non è ancora determinabile, non perché sia esclusa.
 */
export function requiredFisheryOriginIndicationsFor(
  productionMethod: FisheryProductionMethod,
  faoAreaCode?: string | null,
): readonly FisheryOriginIndication[] {
  switch (productionMethod) {
    case 'CAUGHT': {
      const indications: FisheryOriginIndication[] = ['FAO_AREA', 'GEAR_CATEGORY'];
      if (faoAreaCode != null && faoAreaRequiresSubzone(faoAreaCode)) {
        indications.push('FAO_SUBZONE');
      }
      return indications;
    }
    case 'CAUGHT_FRESHWATER':
      return ['WATER_BODY', 'COUNTRY'];
    case 'FARMED':
      return ['COUNTRY'];
    default: {
      const exhaustive: never = productionMethod;
      throw new Error(`Unhandled fishery production method: ${String(exhaustive)}`);
    }
  }
}
